/* ==========================================================================
   Segal — Name Necklace family stamp
   --------------------------------------------------------------------------
   The switcher owns the POST to /name-request and knows nothing about product
   families. Rather than edit that 47KB file — the one behind every regression
   this week — this stamps the family onto the request on its way out.

   It MUST load before the switcher, which is why the snippet lists it first
   and undeferred.

   SELF-HEALING. Measured on the diamond preview: after this file ran,
   window.fetch.__segalFamilyPatched read false, because one of the theme's
   other scripts (Klaviyo, Growave, Tidio, Outbrain and the Heartcoding block
   all touch the page late) had replaced window.fetch afterwards. If that
   replacement captured OUR fetch the stamp still applies; if it captured a
   saved original, it does not — and the difference is a $3,470 product
   created as an $815 one. We do not gamble on which: the wrapper is
   re-applied on a short interval and again on the first interaction, so the
   outermost fetch is always ours by the time a customer can press Generate.

   Safety: the Worker treats any family it does not recognise as 'plain'. This
   file only ever sends 'diamond' when the Liquid snippet said so, and the
   snippet only says so when the product carries the 'diamond-name-necklace'
   tag. The failure direction is the cheap one.
   ========================================================================== */
(() => {
  'use strict';
  const cfg = window.SegalNameNecklace || {};
  const FAMILY = cfg.family === 'diamond' ? 'diamond' : 'plain';
  const target = String(cfg.requestUrl || '');

  /* ------------------------------------------------------------------------
     2026-09-01 — CROSS-FAMILY INDEX CACHE PURGE.

     Reported by Yaniv: on /products/diamond-gold-name-necklace-anastasia,
     typing "Nicole" returned a PLAIN GOLD necklace. Root cause: the switcher's
     name index (assets/name-necklace-index.json) holds 638 entries and every
     one of them is a plain `gold-name-necklace-*` handle. Its lookup is
     family-blind, so "nicole" resolved to gold-name-necklace-nicole and
     swapProduct() went there. The Worker was never called — which is why the
     family stamp below did nothing to prevent it. The Worker itself is
     correct: handleFor(name, script, family) builds the diamond handle and
     findProduct() looks up exactly that one.

     The snippet now points indexUrl at a per-family index. But loadIndex()
     caches whatever it fetched under ONE shared localStorage key, and it seeds
     `index` from that cache synchronously before the network fetch resolves.
     A customer who saw a plain PDP and then opened a diamond one would carry
     638 plain handles into that window — the exact bug, re-armed for ~40ms.

     So: if the cached payload belongs to the other family, drop it. The
     switcher then starts from an EMPTY index, and an empty index is the safe
     state — a miss routes to the Worker, which is family-correct and answers
     `known` for an existing name for free, without touching a rate counter.
     Slower by one round trip, never wrong.

     Keep this in step with `cacheKey` in name-necklace-switcher-v3.js.
     ---------------------------------------------------------------------- */
  const INDEX_CACHE_KEY = 'segal-name-index-v2';
  try {
    const raw = localStorage.getItem(INDEX_CACHE_KEY);
    if (raw) {
      const payload = JSON.parse(raw);
      const cachedFamily = payload && payload.family === 'diamond' ? 'diamond' : 'plain';
      if (cachedFamily !== FAMILY) localStorage.removeItem(INDEX_CACHE_KEY);
    }
  } catch (_) {
    // Unparseable or storage denied: remove it if we can, otherwise carry on.
    try { localStorage.removeItem(INDEX_CACHE_KEY); } catch (__) { }
  }

  if (!target) return;

  const M = window.SegalNameNecklaceMetrics = window.SegalNameNecklaceMetrics || {};
  M.family = FAMILY;
  M.familyStampReapplied = 0;

  const wrap = () => {
    const current = window.fetch;
    if (!current || current.__segalFamilyPatched) return false;
    const patched = function (input, init) {
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
        // Only the generation endpoint, only POSTs, only bodies that parse as JSON.
        // Anything else - including a body that fails to parse - passes through untouched.
        if (url.indexOf(target) === 0 && method === 'POST' && init && typeof init.body === 'string') {
          const body = JSON.parse(init.body);
          if (body && typeof body === 'object' && !body.family) {
            body.family = FAMILY;
            init = Object.assign({}, init, { body: JSON.stringify(body) });
            M.lastFamilyStamp = { name: body.name, family: FAMILY, at: new Date().toISOString() };
          }
        }
      } catch (_) { /* never let this break a request */ }
      return current.apply(this, [input, init]);
    };
    patched.__segalFamilyPatched = true;
    window.fetch = patched;
    return true;
  };

  wrap();

  // Re-assert for the first 15 seconds of page life, which covers every late script
  // on this theme, and once more the moment the customer touches the page.
  let ticks = 0;
  const iv = setInterval(() => {
    ticks += 1;
    if (wrap()) M.familyStampReapplied += 1;
    if (ticks > 30) clearInterval(iv);
  }, 500);
  ['pointerdown', 'keydown'].forEach((evt) => {
    document.addEventListener(evt, () => { if (wrap()) M.familyStampReapplied += 1; }, true);
  });
})();
